// Puts Me together from the parts and the host's extensions.
//
// Promise:
// - `createMeApp` makes everything once per app (API, QueryClient, shortcuts,
//   router, label filter); none of it is a module singleton, so two apps (or
//   tests) never share data. Only the toast and confirm layers are page-wide.
//   Part ids must be unique.
// - `start()` opens the event stream (and, with a persister, restores the cache
//   first); `mount(element)` renders the whole app into an element (the offline
//   edition). Cloud uses `meRouteTree` and `MeProviders` inside its own router.
// - The URL never gets search parameters a route did not declare.
// - A part's `Background` is mounted once, inside the router and the query
//   client, on every page (the root frame), in the order of the parts.
import {
  type QueryClient,
  QueryClientProvider,
  type QueryKey,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type AnyRoute,
  createMemoryHistory,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import { type ReactNode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { createApi } from "../data/api";
import { restorePersistedCache } from "../data/persist";
import { createQueryClient } from "../data/query-client";
import { applyStoredTheme, useApplyTheme } from "../device/theme";
import { initLocale } from "../i18n/locale";
import { followAccountLocale, useProfile } from "../me/profile";
import { startMeRealtime } from "../realtime/realtime";
import { createListMemory } from "../shell/list-memory";
import { PanelBody, PanelHeader } from "../shell/panel";
import { HostStowRelay } from "../shell/phone";
import { RoutePendingBadge } from "../shell/route-pending";
import { createSheetState, ShortcutSheet } from "../shell/shortcut-sheet";
import { ShortcutProvider, ShortcutStore } from "../shell/shortcuts";
import { useTitleBarArea } from "../shell/title-bar";
import { Button } from "../ui/button";
import { ConfirmLayer } from "../ui/confirm";
import { installCutTextTooltip } from "../ui/cut-text-tooltip";
import { installFocusRing } from "../ui/focus-ring";
import { installScrollbarAutohide } from "../ui/scrollbar-autohide";
import { ToastLayer } from "../ui/toast";
import {
  type MeApp,
  MeAppContext,
  type MeExtensions,
  useMeApp,
} from "./context";
import { loadScreensFor, warmLazyRoutes } from "./lazy";
import { MeNotFound, type MeRouterContext, meRouteTree } from "./routes";
import type { HeaderAction, RowMark, ScreenPart } from "./screen-part";
import { shellPart } from "./shell-part";
import { cannotOpen, isUnreachable, watchUnreachable } from "./unreachable";
import { createWorkspaceFilter } from "./workspace-filter";

export interface MeAppOptions {
  parts: readonly ScreenPart[];
  extensions?: MeExtensions;
  /** Prefix of the API (default: the screen's origin). */
  apiBase?: string;
  fetch?: typeof fetch;
  eventsUrl?: string;
  /** Changes with every build (drops a persisted cache of another build). */
  buildId?: string;
  queryClient?: QueryClient;
  /**
   * Where the router starts, its history kept in memory instead of the
   * browser's: for tests, and for a page shown inside another screen that
   * keeps the window's history itself (the desktop app's frame).
   */
  initialUrl?: string;
  /**
   * Whether the other screens' code is loaded ahead while idle, once the first
   * screen is drawn (default true; tests turn it off).
   */
  warmScreens?: boolean;
}

export interface MeAppInstance {
  app: MeApp;
  queryClient: QueryClient;
  shortcuts: ShortcutStore;
  // biome-ignore lint/suspicious/noExplicitAny: the router of a composed tree
  router: ReturnType<typeof createRouter<any, any, any, any, any>>;
  /** Opens the event stream (and restores a persisted cache first). Returns stop. */
  start(): Promise<() => void>;
  /** Renders the app into an element (the offline edition). */
  mount(element: HTMLElement): Promise<() => void>;
  Root(): ReactNode;
}

export function createMeApp(options: MeAppOptions): MeAppInstance {
  const extensions = options.extensions ?? {};
  const api = createApi({
    base: options.apiBase,
    fetch: options.fetch,
    onUnauthorized: extensions.onUnauthorized,
  });
  const queryClient = options.queryClient ?? createQueryClient();
  // biome-ignore lint/suspicious/noExplicitAny: assigned right below
  let router: any;
  const shortcuts = new ShortcutStore({
    navigate: (to) => void router?.navigate({ to }),
  });
  const sheet = createSheetState();
  const shell = shellPart({
    toggleSheet: sheet.toggle,
    router: () => router,
    appearanceRows: extensions.appearanceRows,
    account: extensions.account !== undefined,
  });
  const parts = [shell, ...options.parts];
  const ids = new Set<string>();
  for (const p of parts) {
    if (ids.has(p.id)) throw new Error(`duplicate screen part: ${p.id}`);
    ids.add(p.id);
  }
  const byOrder = <T extends { order: number }>(xs: T[]) =>
    xs.sort((a, b) => a.order - b.order);
  const app: MeApp = {
    api,
    apiBase: options.apiBase ?? "",
    parts,
    extensions,
    nav: byOrder(parts.flatMap((p) => (p.nav ? [p.nav] : []))),
    navSections: byOrder(
      parts.flatMap((p) => (p.navSection ? [p.navSection] : [])),
    ),
    favoriteKinds: Object.assign(
      {},
      ...parts.map((p) => p.favoriteKinds ?? {}),
    ),
    settings: byOrder([
      ...parts.flatMap((p) => p.settings ?? []),
      ...(extensions.settings ?? []),
    ]),
    headerActions: mergeHeaderActions(parts),
    rowMarks: mergeRowMarks(parts),
    sideViews: Object.assign({}, ...parts.map((p) => p.sideViews ?? {})),
    workspaceFilter: createWorkspaceFilter(),
    listMemory: createListMemory(),
    sheet,
  };
  for (const p of parts) if (p.shortcuts) shortcuts.register(p.shortcuts);

  const root = createRootRouteWithContext<MeRouterContext>()({
    component: AppFrame,
    // Outside /me too (the offline edition has nothing else): in Me's frame.
    notFoundComponent: MeNotFound,
  });
  const index = createRoute({
    getParentRoute: () => root,
    path: "/",
    beforeLoad: () => {
      throw redirect({ to: "/me", replace: true });
    },
  });
  const tree = root.addChildren([
    index,
    meRouteTree(root, app),
    ...((extensions.routes?.(root) ?? []) as AnyRoute[]),
  ]);
  router = createRouter({
    routeTree: tree,
    context: { queryClient, api },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    defaultPendingMs: 150,
    defaultPendingMinMs: 300,
    defaultErrorComponent: RouteError,
    history: options.initialUrl
      ? createMemoryHistory({ initialEntries: [options.initialUrl] })
      : undefined,
  });
  app.listMemory.attach(router.history);
  const history = router.history;
  watchUnreachable(queryClient, () => history.back());
  // Once the first screen is drawn, the other screens' code loads while the
  // browser is idle: Me's, and those of the host's routes it names.
  const warmAfterFirst = router.subscribe("onResolved", () => {
    warmAfterFirst();
    if (options.warmScreens === false) return;
    warmLazyRoutes(
      router,
      (path) =>
        path === "/me" ||
        path.startsWith("/me/") ||
        (extensions.warmRoutes?.(path) ?? false),
    );
  });

  const shouldPersist = (key: QueryKey) =>
    parts.some((p) => p.persist?.(key) ?? false);

  const start = async () => {
    let stopPersist = () => {};
    if (extensions.persister)
      stopPersist = await restorePersistedCache({
        queryClient,
        persister: extensions.persister,
        shouldPersist,
        buster: options.buildId ?? "dev",
      });
    const stream = startMeRealtime({
      qc: queryClient,
      api,
      handlers: parts.flatMap((p) => (p.realtime ? [p.realtime] : [])),
      url: options.eventsUrl,
      fetch: options.fetch,
    });
    return () => {
      stream.close();
      stopPersist();
    };
  };

  function Root() {
    return (
      <MeProviders app={app} queryClient={queryClient} shortcuts={shortcuts}>
        <RouterProvider router={router} />
      </MeProviders>
    );
  }

  return {
    app,
    queryClient,
    shortcuts,
    router,
    start,
    async mount(element) {
      applyStoredTheme();
      initLocale();
      // The opened screen's code loads while the cache is restored.
      loadScreensFor(router, router.history.location.pathname);
      const stop = await start();
      const reactRoot = createRoot(element);
      reactRoot.render(<Root />);
      return () => {
        reactRoot.unmount();
        stop();
      };
    },
    Root,
  };
}

/** Header actions of every part, by the kind of detail they go in (in part order). */
function mergeHeaderActions(
  parts: readonly ScreenPart[],
): Record<string, HeaderAction[]> {
  const out: Record<string, HeaderAction[]> = {};
  for (const p of parts)
    for (const [kind, actions] of Object.entries(p.headerActions ?? {}))
      out[kind] = [...(out[kind] ?? []), ...actions];
  return out;
}

/** Row marks of every part, by the kind of row they go on (in part order). */
function mergeRowMarks(
  parts: readonly ScreenPart[],
): Record<string, RowMark[]> {
  const out: Record<string, RowMark[]> = {};
  for (const p of parts)
    for (const [kind, marks] of Object.entries(p.rowMarks ?? {}))
      out[kind] = [...(out[kind] ?? []), ...marks];
  return out;
}

/** The providers Me needs, for hosts that render their own router (cloud). */
export function MeProviders({
  app,
  queryClient,
  shortcuts,
  children,
}: {
  app: MeApp;
  queryClient: QueryClient;
  shortcuts: ShortcutStore;
  children: ReactNode;
}) {
  // frontend-development: not a fetch; shows the scrollbars only while they
  // scroll (styles/index.css), for every host.
  useEffect(() => installScrollbarAutohide(), []);
  // Neither: a text cut short shows itself whole under a resting mouse.
  useEffect(() => installCutTextTooltip(), []);
  // Neither: the focus outline only after Tab or an arrow key.
  useEffect(() => installFocusRing(), []);
  return (
    <QueryClientProvider client={queryClient}>
      <MeAppContext.Provider value={app}>
        <ShortcutProvider store={shortcuts}>{children}</ShortcutProvider>
      </MeAppContext.Provider>
    </QueryClientProvider>
  );
}

/** Mounted once around every screen: theme, language, layers, the parts' backgrounds. */
function AppFrame() {
  useApplyTheme();
  const { parts } = useMeApp();
  const qc = useQueryClient();
  // Keeps the profile fetched so the account's language arrives.
  useProfile();
  // frontend-development: not a fetch; follows the account's language once the server answers.
  useEffect(() => followAccountLocale(qc), [qc]);
  // A phone app's frame lends the confirmation its way to put the app's parts away.
  const [stowRelay] = useState(() => ({
    current: null as ((keepBar?: boolean) => () => void) | null,
  }));
  return (
    <HostStowRelay.Provider value={stowRelay}>
      {parts.map(({ id, Background }) =>
        Background ? <Background key={id} /> : null,
      )}
      <Outlet />
      <RoutePendingBadge />
      <ToastLayer />
      <ConfirmLayer />
      <ShortcutSheet />
    </HostStowRelay.Provider>
  );
}

/**
 * A screen that could not be drawn: why, and reading it again. In an app it
 * starts below the status bar (its inset; zero in a browser — the phone's
 * `--phone-top` is not set when the screens never started), and the button
 * is the way out where no browser bar can reload it.
 */
function RouteError({ error }: { error: unknown }) {
  const router = useRouter();
  const { extensions } = useMeApp();
  // Under the window's buttons (the desktop app's title bar), never under them.
  const bar = useTitleBarArea(extensions.titleBar);
  // What the host says instead, for an error it knows (signed out of the cloud).
  const instead = extensions.errorScreen?.(error) ?? null;
  // The data is out of reach now (the Hub not reached) and was not read
  // before: said briefly, and back where the person was (an error screen,
  // or a screen that never came, was all there was: the person 2026-10-30).
  const unreachable = isUnreachable(error);
  useEffect(() => {
    if (unreachable) cannotOpen(() => router.history.back());
  }, [unreachable, router]);
  if (unreachable) return null;
  const top = bar ? `${bar.height}px` : "env(safe-area-inset-top, 0px)";
  if (instead)
    return (
      <div
        data-title-bar=""
        className="flex h-dvh flex-col bg-surface"
        style={{ paddingTop: top }}
      >
        {instead}
      </div>
    );
  return (
    <div
      data-title-bar=""
      className="flex h-dvh flex-col bg-surface"
      style={{ paddingTop: top }}
    >
      <PanelHeader title={m.shell_error_title()} />
      <PanelBody>
        <p className="text-[13px] text-muted">{m.shell_error_body()}</p>
        <p className="mt-3 select-text font-mono text-[11.5px] text-faint">
          {error instanceof Error ? error.message : String(error)}
        </p>
        <Button className="mt-4" onClick={() => window.location.reload()}>
          {m.shell_error_reload()}
        </Button>
      </PanelBody>
    </div>
  );
}
